import constate from 'constate';
import { useMemo } from 'react';
import { useEditorState } from '../hooks/useEditorState';
import type { UseEditorSdkProps } from './useEditorSdk';
import { useEditorSdk } from './useEditorSdk';

export type EditorContextProps = UseEditorSdkProps;

const useEditorContext = (props: EditorContextProps) => {
  const editorSdk = useEditorSdk(props);
  const isLoaded = useEditorState(editorSdk, 'loaded');
  const draft = useEditorState(editorSdk, 'draft');
  const edits = useEditorState(editorSdk, 'edits');
  const redos = useEditorState(editorSdk, 'redos');
  const inspectAction = useEditorState(editorSdk, 'inspectAction');
  const variables = useEditorState(editorSdk, 'variables');
  const findAndReplaceRules = useEditorState(editorSdk, 'findAndReplaceRules');
  const codeSnippets = useEditorState(editorSdk, 'codeSnippets');
  const chartTemplates = useEditorState(editorSdk, 'chartsTemplates');
  const metadata = useEditorState(editorSdk, 'metadata');
  const transactionModifications = useEditorState(
    editorSdk,
    'transactionModifications'
  );
  const navigationModifications = useEditorState(
    editorSdk,
    'navigationModifications'
  );
  const spotEditsModifications = useEditorState(
    editorSdk,
    'spotEditsModifications'
  );
  const tableCraftObjects = useEditorState(editorSdk, 'tableCraftObjects');
  const tableCraftRows = useEditorState(editorSdk, 'tableCraftRows');
  const tableCraftFunctions = useEditorState(editorSdk, 'tableCraftFunctions');

  const guidanceTooltipsModifications = useEditorState(
    editorSdk,
    'guidanceTooltipsModifications'
  );

  const isGuidanceTooltipsEditLoading = useEditorState(
    editorSdk,
    'isGuidanceTooltipsEditLoading'
  );

  const isGuidanceTooltipOpen = useEditorState(
    editorSdk,
    'isGuidanceTooltipOpen'
  );
  const aiPromptGroup = useEditorState(editorSdk, 'aiPromptGroup');

  const performanceResults = useEditorState(editorSdk, 'performanceResults');

  const isPerformanceMeasuringStarted = useEditorState(
    editorSdk,
    'isPerformanceMeasuringStarted'
  );

  const performanceSelectorXpath = useEditorState(
    editorSdk,
    'performanceSelectorXpath'
  );

  return {
    editorSdk,
    isLoaded,
    draft,
    edits,
    redos,
    inspectAction,
    variables,
    findAndReplaceRules,
    codeSnippets,
    chartTemplates,
    metadata,
    transactionModifications,
    navigationModifications,
    spotEditsModifications,
    tableCraftObjects,
    tableCraftRows,
    tableCraftFunctions,
    guidanceTooltipsModifications,
    isGuidanceTooltipsEditLoading,
    isGuidanceTooltipOpen,
    aiPromptGroup,
    performanceResults,
    isPerformanceMeasuringStarted,
    performanceSelectorXpath,
  };
};

export const [
  EditorProvider,
  useEditor,
  useEditorDraft,
  useEditorMetadata,
  useEditorLoaded,
  useEditorEdits,
  useEditorInspect,
  useEditorVariables,
  useEditorFindAndReplaceRules,
  useEditorCodeSnippets,
  useEditorCharts,
  useEditorTransactionModifications,
  useEditorNavigationModifications,
  useEditorImportEdits,
  useSpreadsheetApi,
  useEditorSpotEditsModifications,
  useEditorTableCraftData,
  useEditorGuidanceTooltips,
  useEditorAiPromptGroup,
  useEditorPerformance,
] = constate(
  useEditorContext,
  ({ editorSdk }) => editorSdk,

  ({ draft }) => useMemo(() => draft, [draft]),

  ({ metadata }) => useMemo(() => metadata, [metadata]),

  ({ isLoaded }) => isLoaded,

  ({ editorSdk, edits, redos }) =>
    useMemo(
      () => ({
        edits,
        redos,
        redoEdit: editorSdk.redoEdit,
        undoEdit: editorSdk.undoEdit,
      }),
      [editorSdk, edits, redos]
    ),

  ({ editorSdk, inspectAction }) =>
    useMemo(
      () => ({
        inspectAction,
        updateInspectAction: editorSdk.updateInspectAction,
      }),
      [editorSdk, inspectAction]
    ),

  ({ editorSdk, variables }) =>
    useMemo(
      () => ({
        variables,
        createVariable: editorSdk.createVariable,
        editVariable: editorSdk.editVariable,
        deleteVariable: editorSdk.deleteVariable,
        reorderVariables: editorSdk.reorderVariables,
      }),
      [editorSdk, variables]
    ),

  ({ editorSdk, findAndReplaceRules }) =>
    useMemo(
      () => ({
        rules: findAndReplaceRules,
        createFindAndReplaceRule: editorSdk.createFindAndReplaceRule,
        editFindAndReplaceRule: editorSdk.editFindAndReplaceRule,
        deleteFindAndReplaceRule: editorSdk.deleteFindAndReplaceRule,
        reorderFindAndReplaceRules: editorSdk.reorderFindAndReplaceRules,
      }),
      [editorSdk, findAndReplaceRules]
    ),

  ({ editorSdk, codeSnippets }) =>
    useMemo(
      () => ({
        snippets: codeSnippets,
        createCodeSnippet: editorSdk.createCodeSnippet,
        editCodeSnippet: editorSdk.editCodeSnippet,
        deleteCodeSnippet: editorSdk.deleteCodeSnippet,
        reorderCodeSnippets: editorSdk.reorderCodeSnippets,
      }),
      [editorSdk, codeSnippets]
    ),

  ({ editorSdk, chartTemplates }) =>
    useMemo(
      () => ({
        chartTemplates,
        fetchChartTemplates: editorSdk.fetchChartTemplates,
        createChartTemplate: editorSdk.createChartTemplate,
        updateChartTemplate: editorSdk.updateChartTemplate,
        deleteChartTemplate: editorSdk.deleteChartTemplate,
      }),
      [editorSdk, chartTemplates]
    ),

  ({ editorSdk, transactionModifications }) =>
    useMemo(
      () => ({
        transactions: editorSdk.transactions,
        transactionModifications,
        createTransactionRule: editorSdk.createTransactionRule,
        editTransactionModification: editorSdk.editTransactionModification,
        deleteTransactionModification: editorSdk.deleteTransactionModification,
        reorderTransactionModification:
          editorSdk.reorderTransactionModification,
      }),
      [editorSdk, transactionModifications]
    ),

  ({ editorSdk, navigationModifications }) =>
    useMemo(
      () => ({
        navigationModifications,
        startBlockInteractionsMode: editorSdk.startBlockInteractionsMode,
        createNavigationModification: editorSdk.createNavigationModification,
        editNavigationModification: editorSdk.editNavigationModification,
        deleteNavigationModification: editorSdk.deleteNavigationModification,
        reorderNavigationModification: editorSdk.reorderNavigationModification,
        createBlockInteractionsSettings:
          editorSdk.createNavigationModificationBlockInteractionsSettings,
        blockInteractionsSettings: editorSdk.blockInteractionsSettings,
      }),
      [editorSdk, navigationModifications]
    ),

  ({ editorSdk }) =>
    useMemo(
      () => ({
        onImportEdits: editorSdk.onImportEdits,
      }),
      [editorSdk]
    ),
  ({ editorSdk }) =>
    useMemo(
      () => ({
        createSpreadsheet: editorSdk.createSpreadsheet,
        getSpreadsheetValues: editorSdk.getSpreadsheetValues,
      }),
      [editorSdk]
    ),
  ({ editorSdk, spotEditsModifications }) =>
    useMemo(
      () => ({
        spotEditsModifications,
        editSpotEditModification: () => null,
        deleteSpotEditModification: () => null,
        reorderSpotEditModification: () => null,
      }),
      [editorSdk, spotEditsModifications]
    ),
  ({ editorSdk, tableCraftObjects, tableCraftRows, tableCraftFunctions }) =>
    useMemo(
      () => ({
        tableCraftObjects,
        tableCraftRows,
        tableCraftFunctions,
      }),
      [editorSdk, tableCraftObjects, tableCraftRows, tableCraftFunctions]
    ),

  ({
    editorSdk,
    guidanceTooltipsModifications,
    isGuidanceTooltipOpen,
    isGuidanceTooltipsEditLoading,
  }) =>
    useMemo(
      () => ({
        guidanceTooltipsModifications,
        stopGuidanceTooltipsMode: editorSdk.stopGuidanceTooltipsMode,
        startGuidanceTooltipsMode: editorSdk.startGuidanceTooltipsMode,
        deleteGuidanceTooltipModification:
          editorSdk.deleteGuidanceTooltipModification,
        toggleGuidanceTooltipModificationVisibility:
          editorSdk.toggleGuidanceTooltipModificationVisibility,
        highlightGuidanceTooltip: editorSdk.highlightGuidanceTooltip,
        destroyHighlightGuidanceTooltip:
          editorSdk.destroyHighlightGuidanceTooltip,
        navigateToGuidanceTooltip: editorSdk.navigateToModification,
        showAllGuidanceTooltipModifications:
          editorSdk.showAllGuidanceTooltipModifications,
        hideAllGuidanceTooltipModifications:
          editorSdk.hideAllGuidanceTooltipModifications,
        isGuidanceTooltipsEditLoading: isGuidanceTooltipsEditLoading,
        setIsGuidanceTooltipsEditLoading:
          editorSdk.setIsGuidanceTooltipsEditLoading,
        setIsGuidanceTooltipOpen: editorSdk.setIsGuidanceTooltipOpen,
        isGuidanceTooltipOpen: isGuidanceTooltipOpen,
      }),
      [
        guidanceTooltipsModifications,
        editorSdk,
        isGuidanceTooltipOpen,
        isGuidanceTooltipsEditLoading,
      ]
    ),

  ({ aiPromptGroup }) => useMemo(() => aiPromptGroup, [aiPromptGroup]),
  ({
    editorSdk,
    performanceResults,
    isPerformanceMeasuringStarted,
    performanceSelectorXpath,
  }) =>
    useMemo(
      () => ({
        togglePerformanceMeasuring: editorSdk.togglePerformanceMeasuring,
        performanceResults,
        isPerformanceMeasuringStarted,
        handlePerformanceResultsAvgCalculated:
          editorSdk.handlePerformanceResultsAvgCalculated,
        handlePickSelector: editorSdk.handlePerformancePickSelector,
        performanceSelectorXpath,
        handleStopOnSelectorXpathChange:
          editorSdk.handlePerformanceStopOnSelectorXpathChange,
        hanleRestartButtonClick: editorSdk.handlePerformanceRestartButtonClick,
      }),
      [
        editorSdk,
        performanceResults,
        isPerformanceMeasuringStarted,
        performanceSelectorXpath,
      ]
    )
);
